import { useState } from "react";
import { FileTextIcon, Loader2Icon } from "lucide-react";
import type { KnowledgeManifest, KnowledgeScopeType } from "@type/training";
import { KnowledgeDocDrawer } from "@component/training/knowledge-doc-drawer";
import { getKbDocument, type KbDocument } from "@service/knowledge-base";
import { cn } from "@util/tailwind";

/** One line answering "where did this reference come from" per scope. */
const SCOPE_SOURCE_LINE: Record<KnowledgeScopeType, string> = {
    workspace: "From the workspace Knowledge Base",
    campaign: "From this campaign's documents",
    aiqm_batch: "Attached to this upload",
};

/**
 * "Sources" — a quiet Overview-tab section (styled like Conversation Flow /
 * Action Items) listing the documents the AI evaluator was actually given at
 * scoring time, from the manifest snapshotted on the result. It deliberately
 * reads from the snapshot, not the live KB, and never claims per-category
 * attribution — only what verifiably entered the scoring context.
 *
 * Clicking a document opens the shared KnowledgeDocDrawer (Preview + Extracted
 * Information) — the same view as the Knowledge Base tab.
 *
 * Renders nothing when there's no manifest (legacy results scored before the
 * manifest landed), so it's safe on every surface without a feature flag.
 */
export function KnowledgeSourcesSection({
    manifest,
    className,
}: {
    manifest?: KnowledgeManifest | null;
    className?: string;
}) {
    const [drawerDoc, setDrawerDoc] = useState<KbDocument | null>(null);
    const [loadingDocId, setLoadingDocId] = useState<string | null>(null);
    const [rowError, setRowError] = useState<string | null>(null);

    const docs = manifest?.documents ?? [];

    // Three genuinely different states, previously all rendered as nothing:
    //
    //   manifest === undefined → legacy result, scored before provenance existed
    //   manifest === null      → KB resolution FAILED for this result (the BE
    //                            builds a manifest even for zero documents, so
    //                            null means it never ran — ourteam-backend#3605)
    //   documents: []          → resolution ran and this campaign genuinely has
    //                            no knowledge documents
    //
    // Collapsing all three into `return null` made a broken evaluation look
    // identical to a feature that isn't there, which is what made #3605 take
    // several rounds of testing to isolate. Legacy results still render nothing;
    // the other two now say which one happened.
    if (manifest === undefined) return null;

    if (docs.length === 0) {
        const failed = manifest === null;
        return (
            <div className={className}>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                    Sources
                </p>
                <p className="text-sm text-gray-500">
                    {failed
                        ? "Knowledge documents couldn't be resolved for this evaluation, so it was scored without them."
                        : "No knowledge documents informed this evaluation."}
                </p>
                {failed && (
                    <p className="text-xs text-gray-400 mt-1.5">
                        Any documents attached to this campaign were not applied. Re-run the
                        session to score it against them.
                    </p>
                )}
            </div>
        );
    }

    // Show the FULL manifest, not just the used docs. The whole point of this
    // section is truthful, auditable provenance: a document that was eligible
    // but dropped by the context budget must remain visible (clearly marked as
    // not used), never silently omitted — otherwise the reader can't tell "not
    // a source" from "available but not evaluated". Excluded rows are dimmed and
    // labelled below. `truncated` (partially-used) rows stay clean per prior UX
    // feedback — those docs WERE used, so they aren't the truthfulness gap.
    const includedCount = docs.filter((d) => d.included).length;
    const hasExcluded = includedCount < docs.length;

    async function openDoc(documentId: string) {
        if (loadingDocId) return;
        setLoadingDocId(documentId);
        setRowError(null);
        try {
            const doc = await getKbDocument(documentId);
            setDrawerDoc(doc);
        } catch {
            setRowError("Couldn't open this document. It may no longer be available.");
        } finally {
            setLoadingDocId(null);
        }
    }

    return (
        <div className={className}>
            <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Sources
                </p>
                {/* Bare count when everything fit (stays quiet); the used/total
                    fraction only appears when some docs were dropped. */}
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100 tabular-nums">
                    {hasExcluded ? `${includedCount} of ${docs.length}` : includedCount}
                </span>
            </div>
            {/* Plain divs, not ul/li — a global list style was re-adding bullets. */}
            <div className="space-y-2.5">
                {docs.map((d) => (
                    <button
                        key={d.document_id}
                        onClick={() => openDoc(d.document_id)}
                        className={cn(
                            "group flex items-start gap-2.5 text-left w-full",
                            !d.included && "opacity-55",
                        )}
                        title="View document and extracted information"
                    >
                        {loadingDocId === d.document_id ? (
                            <Loader2Icon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 mt-0.5 animate-spin" />
                        ) : (
                            <FileTextIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 mt-0.5" />
                        )}
                        <span className="min-w-0">
                            <span
                                className={cn(
                                    "block text-sm text-gray-800 truncate",
                                    "group-hover:text-blue-700 group-hover:underline",
                                )}
                            >
                                {d.name}
                            </span>
                            {/* Origin line; excluded docs say why they weren't
                                used so the provenance stays truthful. Used docs
                                (incl. truncated) show origin only, per UX. */}
                            <span className="block text-xs text-gray-500 mt-0.5">
                                {SCOPE_SOURCE_LINE[d.scope]}
                                {!d.included && " · Not used — over the context limit"}
                            </span>
                        </span>
                    </button>
                ))}
            </div>
            {rowError && <p className="text-xs text-rose-600 mt-2">{rowError}</p>}
            <p className="text-[11px] text-gray-400 mt-2.5">
                Snapshot taken at scoring time. Later document changes don't affect
                this result.
            </p>

            <KnowledgeDocDrawer doc={drawerDoc} onClose={() => setDrawerDoc(null)} />
        </div>
    );
}
